// Pin a chart Neo showed to the boat's board (task neo_board, cloud ADR 0239):
// one press, one REST call, no model call. It sits in the head of every inline
// chart that has a stored answer behind it, in the chat and on the Watch page,
// and of every map Neo showed in the chat (TOM-468), which pins by its card id.

import { useEffect, useState } from 'react';

import { NEO_BOARD_ITEM_REMOVED_EVENT, pinBoardRecord, type PinRecordRef } from '../lib/neoBoard';
import { PinIcon } from './app/icons';

type PinState = 'idle' | 'pinning' | 'pinned' | 'full' | 'error';

const LABEL: Readonly<Record<PinState, string>> = {
  idle: 'Pin to the board',
  pinning: 'Pinning to the board…',
  pinned: 'Pinned to the board',
  full: 'The board is full — take something off it first',
  error: "Couldn't pin — try again",
};

const TEXT: Readonly<Record<PinState, string>> = {
  idle: 'Pin',
  pinning: 'Pin',
  pinned: 'Pinned',
  full: 'Board full',
  error: 'Retry',
};

export function PinChartButton({
  boatId,
  messageId,
  ...ref
}: {
  boatId: string;
  messageId: string;
} & PinRecordRef) {
  const [state, setState] = useState<PinState>('idle');
  const [itemId, setItemId] = useState<string | null>(null);

  // Taken off the board — on the Board page or by Neo — it can be pinned again.
  useEffect(() => {
    if (itemId === null) return;
    const onRemoved = (e: Event) => {
      if ((e as CustomEvent<{ itemId?: string }>).detail?.itemId !== itemId) return;
      setItemId(null);
      setState('idle');
    };
    window.addEventListener(NEO_BOARD_ITEM_REMOVED_EVENT, onRemoved);
    return () => window.removeEventListener(NEO_BOARD_ITEM_REMOVED_EVENT, onRemoved);
  }, [itemId]);

  async function onPin() {
    if (state === 'pinning' || state === 'pinned') return;
    setState('pinning');
    const out = await pinBoardRecord(
      boatId,
      messageId,
      'cardId' in ref ? { cardId: ref.cardId } : { chartId: ref.chartId }
    );
    if (out.kind === 'pinned' || out.kind === 'already') {
      setItemId(out.itemId);
      setState('pinned');
    } else {
      setState(out.kind === 'full' ? 'full' : 'error');
    }
  }

  return (
    <button
      type="button"
      className={`neo-chart-pin${state === 'pinned' ? ' is-pinned' : ''}`}
      aria-label={LABEL[state]}
      title={LABEL[state]}
      aria-pressed={state === 'pinned'}
      disabled={state === 'pinning'}
      onClick={() => void onPin()}
    >
      <PinIcon />
      <span>{TEXT[state]}</span>
    </button>
  );
}
